import React, { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, useInView } from 'framer-motion';

import THEME from '../../constants/theme';

/**
 * AnimatedBlueBox component - Blue background box that grows from center on scroll
 *
 * @param {Object} props - Component props
 * @param {string} props.text - Text content to display
 * @param {string} props.fontSize - Font size (default: '1.3rem')
 * @param {string} props.padding - Padding (default: THEME.SPACING.XL)
 * @param {string} props.marginTop - Top margin
 * @param {string} props.marginBottom - Bottom margin
 * @returns {React.Component} AnimatedBlueBox component
 */
const AnimatedBlueBox = ({
  text,
  fontSize = '1.3rem',
  padding = `${THEME.SPACING.XL} ${THEME.SPACING.XL}`,
  marginTop = THEME.SPACING.LG,
  marginBottom = THEME.SPACING.LG,
  ...props
}) => {
  const boxRef = useRef(null);
  const isInView = useInView(boxRef, { threshold: 0.3 });

  const boxVariants = {
    hidden: {
      scale: 0,
      opacity: 0
    },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 1.0,
        ease: [0.43, 0.13, 0.23, 0.96] // Custom easing for smooth grow
      }
    }
  };

  return (
    <Box
      ref={boxRef}
      component={motion.div}
      initial='hidden'
      animate={isInView ? 'visible' : 'hidden'}
      variants={boxVariants}
      sx={{
        textAlign: 'center',
        padding: padding,
        backgroundColor: THEME.COLORS.PRIMARY_BLUE,
        color: THEME.COLORS.NEUTRAL_WHITE,
        marginTop: marginTop,
        marginBottom: marginBottom,
        transformOrigin: 'center center',
        ...props.sx
      }}
    >
      <Typography
        variant='body1'
        sx={{
          fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
          fontSize: fontSize,
          lineHeight: 1.6,
          fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.REGULAR
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

export default AnimatedBlueBox;
